import { GRID_COLS, GRID_ROWS } from './constants';

function serializeState(blocks) {
  return blocks
    .map(b => `${b.id}:${b.x},${b.y}`)
    .sort()
    .join('|');
}

function cloneBlocks(blocks) {
  return blocks.map(b => ({ ...b }));
}

function isCollision(block, otherBlocks, excludeId = null) {
  for (const other of otherBlocks) {
    if (other.id === excludeId || other.id === block.id) continue;
    
    const blockRight = block.x + block.width;
    const blockBottom = block.y + block.height;
    const otherRight = other.x + other.width;
    const otherBottom = other.y + other.height;
    
    if (
      block.x < otherRight &&
      blockRight > other.x &&
      block.y < otherBottom &&
      blockBottom > other.y
    ) {
      return true;
    }
  }
  return false;
}

function isInBounds(block) {
  return (
    block.x >= 0 &&
    block.y >= 0 &&
    block.x + block.width <= GRID_COLS &&
    block.y + block.height <= GRID_ROWS
  );
}

function getValidMoves(blocks) {
  const moves = [];
  
  for (const block of blocks) {
    const directions = block.orientation === 'H' 
      ? [{ dx: -1, dy: 0 }, { dx: 1, dy: 0 }]
      : [{ dx: 0, dy: -1 }, { dx: 0, dy: 1 }];
    
    for (const { dx, dy } of directions) {
      let step = 1;
      while (true) {
        const newBlock = {
          ...block,
          x: block.x + dx * step,
          y: block.y + dy * step,
        };
        
        if (!isInBounds(newBlock)) break;
        if (isCollision(newBlock, blocks, block.id)) break;
        
        moves.push({
          blockId: block.id,
          newX: newBlock.x,
          newY: newBlock.y,
        });
        step++;
      }
    }
  }
  
  return moves;
}

function applyMove(blocks, move) {
  return blocks.map(b => 
    b.id === move.blockId 
      ? { ...b, x: move.newX, y: move.newY }
      : { ...b }
  );
}

function isPrisonerEscaped(blocks) {
  const prisoner = blocks.find(b => b.isPrisoner);
  if (!prisoner) return false;
  return prisoner.x + prisoner.width >= GRID_COLS;
}

/** Generous limits for post-generation verification (avoids false "unsolvable"). */
export const LEVEL_VERIFY_MAX_DEPTH = 60;
export const LEVEL_VERIFY_MAX_STATES = 100000;

export function isLevelSolvable(
  blocks,
  {
    maxDepth = LEVEL_VERIFY_MAX_DEPTH,
    maxStates = LEVEL_VERIFY_MAX_STATES,
  } = {}
) {
  return solve(blocks, maxDepth, maxStates);
}

export function solve(initialBlocks, maxDepth = 50, maxStates = 50000) {
  const visited = new Set();
  const queue = [{ blocks: cloneBlocks(initialBlocks), moves: 0 }];
  let head = 0;
  
  visited.add(serializeState(initialBlocks));
  
  while (head < queue.length) {
    if (visited.size > maxStates) break;
    const { blocks, moves } = queue[head++];
    
    if (isPrisonerEscaped(blocks)) {
      return { solvable: true, minMoves: moves, path: [] };
    }
    
    if (moves >= maxDepth) continue;
    
    const validMoves = getValidMoves(blocks);
    
    for (const move of validMoves) {
      const newBlocks = applyMove(blocks, move);
      const stateKey = serializeState(newBlocks);
      
      if (!visited.has(stateKey)) {
        visited.add(stateKey);
        queue.push({
          blocks: newBlocks,
          moves: moves + 1,
        });
      }
    }
  }
  
  return { solvable: false, minMoves: -1, path: [] };
}

export function canMove(block, dx, dy, allBlocks) {
  const newBlock = {
    ...block,
    x: block.x + dx,
    y: block.y + dy,
  };
  
  if (!isInBounds(newBlock)) return false;
  if (isCollision(newBlock, allBlocks, block.id)) return false;
  
  return true;
}

export function getMaxMove(block, dx, dy, allBlocks) {
  let maxStep = 0;
  let step = 1;
  
  while (true) {
    const newBlock = {
      ...block,
      x: block.x + dx * step,
      y: block.y + dy * step,
    };
    
    if (!isInBounds(newBlock)) break;
    if (isCollision(newBlock, allBlocks, block.id)) break;
    
    maxStep = step;
    step++;
  }
  
  return maxStep;
}
